import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material';
import { ArrowLeftIcon } from '@mui/x-date-pickers';
import { Button, CircularProgress, formatError } from '@classity-edtech/ui-base';
import {
  ArrowRightIcon,
  BankNoteIcon,
  Calendar,
  ShoppingIcon,
  UsersIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { RadioButton } from '@classity-edtech/ui-base/dist/components/Inputs';
import { VideocamOutlined } from '@mui/icons-material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { Price } from '../../services/models/product';
import { ProductEvent } from '../../services/models/productEvents';
import { CurrencyCode, NotificationLabel } from '@classity-edtech/platform-utils';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { useAppDispatch } from '../../store/hooks';
import { Notification } from '../../services/models/authentication';
import { useNavigate } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { useCreateOrderMutation } from '../../services/payments';

export interface BookingSummaryProps extends MuiBoxProps {
  prices: Price[];
  products: ProductEvent[];
  user: {
    userId: string;
    uid: string;
    email: string;
    firstname: string;
    lastname: string;
    notifications: Notification[];
  };
}

export const BookingSummary = ({
  className,
  prices: rawPrices,
  products,
  user,
  ...props
}: BookingSummaryProps) => {
  const dispatch: any = useAppDispatch();
  const navigate = useNavigate();
  const defaultCurrency = CurrencyCode.NG;
  const screens = useAppMediaQuery();
  const isSmallScreen = screens.isMobile || screens.isTablet;
  const [expandedTotal, setExpandedTotal] = useState(false);
  const [expandedFeatures, setExpandedFeatures] = useState(true);
  const [priceId, setPriceId] = useState('');

  const [createOrder, createOrderResult] = useCreateOrderMutation();

  React.useEffect(() => {
    if (rawPrices.length === 1 && !priceId) {
      setPriceId(rawPrices[0].id);
    }
  }, [rawPrices, priceId]);

  const toggleExpandedTotal = () => {
    setExpandedTotal(prev => !prev);
  };

  const toggleExpandedFeature = () => {
    setExpandedFeatures(prev => !prev);
  };

  const handlePriceId = (priceId: string) => {
    setPriceId(priceId);
  };

  const prices = rawPrices.map((item, index) => {
    const courseOffer = item.plan?.offers?.course;

    return {
      amount: `₦${item.currencies[0].amount.toLocaleString()}`,
      description:
        item.description?.[0]?.text ||
        item.plan?.name?.find(n => n.language === 'en')?.text ||
        'Standard plan',
      highlight: courseOffer?.value ? courseOffer?.text?.[0]?.text : '',
      id: item.id,
    };
  });

  const total = (prices: Price[]) =>
    prices.reduce(
      (acc, curr) => acc + curr?.currencies?.find(c => c?.code === defaultCurrency)?.amount || 0,
      0,
    );

  const refinedPrices = Array(products?.length)?.fill(
    rawPrices?.find(price => price?.id === priceId) || rawPrices?.[0],
  );

  const handleOrderCreation = async () => {
    if (createOrderResult.isLoading || (!priceId && rawPrices.length > 1)) return;

    const selectedPrice = priceId ? rawPrices.find(p => p.id === priceId) : rawPrices[0];
    if (!selectedPrice) return;

    if (!user?.userId) {
      return navigate(`/${webPaths.signin}`);
    }

    const createOrderResultData = await createOrder({
      items: [
        {
          shippingMethod: 'SERVICE',
          quantity: 1,
          prices: refinedPrices?.map(
            p =>
              ({
                currencies: p?.currencies,
                id: p?.id,
                productId: p?.productId,
              }) as Price,
          ),
          products: products?.map(p => ({
            attendees: p?.attendees?.map(a => ({
              ...a,
              user: undefined,
            })),
            calendarId: p?.calendarId,
            description: p?.description,
            endTime: p?.endTime,
            id: p?.id,
            code: p?.code,
            location: p?.location,
            maxAttendees: p?.maxAttendees,
            category: p?.category,
            name: p?.name,
            startTime: p?.startTime,
            isVirtual: p?.isVirtual,
            organizer: p.organizer,
            recurringEventId: p?.recurringEventId,
          })) as ProductEvent[],
          currencyCode: CurrencyCode.NG,
          totalAmount: total(refinedPrices),
          totalAdjustedAmount: 0,
        },
      ],

      description: [
        {
          language: 'en',
          text: 'Product termly order',
        },
      ],
      customer: {
        uid: user?.uid,
        email: user?.email,
        name: `${user?.firstname} ${user?.lastname}`,
        notification: user?.notifications?.find(n => n?.medium === NotificationLabel.EMAIL),
      },
      totalShippingAmount: 0,
      totalAdjustedAmount: 0,
      serviceFee: 0,
      currencyCode: CurrencyCode.NG,
    });

    if ('error' in createOrderResultData) {
      const error = formatError(createOrderResultData.error.message);
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'An error occurred!',
        }),
      );
    }

    if ('data' in createOrderResultData) {
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'success',
          message: 'Order added to cart successfully!',
        }),
      );
    }
  };

  return (
    <Box sx={{ width: { xs: '100%', md: '280px' } }}>
      <Accordion
        expanded={expandedTotal}
        onChange={() => {}}
        disableGutters
        elevation={0}
        square
        sx={{
          borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          borderTop: `solid 0px transparent`,
          bgcolor: 'transparent',
          '& .MuiButtonBase-root': {
            px: 0,
          },
          '&::before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
          onClick={toggleExpandedTotal}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant='subtitle5'>Booking summary</Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: globalColours['$global-colour-interactive-primary-defaultå'],
                cursor: 'pointer',
              }}
            >
              <ArrowLeftIcon
                sx={{
                  width: '28px',
                  height: '28px',
                  transform: expandedTotal ? 'rotate(90deg)' : 'rotate(-90deg)',
                }}
              />
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 0,
            py: 1.5,
            borderTop: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Typography
              variant='body3'
              sx={{
                width: 'calc(100% - 50px)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Learn exam techniques that works
            </Typography>
            <Typography variant='body3'>₦{total(refinedPrices)?.toLocaleString()}</Typography>
          </Box>
          <Typography variant='body3'>#coaching</Typography>
          <Button
            variant='text'
            sx={{ p: 0, width: 'fit-content', minWidth: 'fit-content' }}
            disableBottomFixed
            onClick={() => {}}
          >
            <Typography variant='body3'>Modify</Typography>
          </Button>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1.5, gap: 1 }}>
        <Typography variant='body2SemiBold'>Total</Typography>
        <Typography variant='body3'>₦{total(refinedPrices)?.toLocaleString()}</Typography>
      </Box>

      <Box sx={{ my: 0.5, display: { xs: 'none', md: 'block' } }}>
        <Box
          sx={{
            p: 0.5,
            background: globalColours['$global-colour-sentiment-info-default'],
            width: '100%',
          }}
        >
          <Typography variant='body3' sx={{ color: globalColours['$global-colour-base-contrast'] }}>
            <strong>Can't pay?</strong> Share the payment link at Checkout and let a relative pay
            for you!
          </Typography>
        </Box>
      </Box>

      <Button
        sx={{ width: '100% !important', display: 'flex', alignItems: 'center', gap: 1, zIndex: 20 }}
        onClick={handleOrderCreation}
        disabled={createOrderResult.isLoading || (rawPrices.length > 1 && !priceId)}
        proceedingIcon={
          createOrderResult.isLoading ? undefined : (
            <>
              {!isSmallScreen ? (
                <ShoppingIcon
                  sx={{
                    width: '14px',
                    height: '14px',
                    color: globalColours['$global-colour-base-contrast'],
                  }}
                />
              ) : (
                <ArrowRightIcon
                  sx={{
                    width: '14px',
                    height: '14px',
                    color: globalColours['$global-colour-base-contrast'],
                  }}
                />
              )}
            </>
          )
        }
      >
        {createOrderResult.isLoading ? (
          <CircularProgress />
        ) : (
          <>{!isSmallScreen ? 'ADD TO CART' : 'ENROLL'}</>
        )}
      </Button>

      <Stack sx={{ gap: 0.5 }}>
        {prices.length > 1 &&
          prices.map((p, i) => (
            <Box
              key={i}
              sx={{ py: 2, px: 2, display: 'flex', alignItems: 'center', gap: 2 }}
              onClick={() => handlePriceId(p.id)}
            >
              <RadioButton checked={p.id === priceId} />

              <Stack sx={{ gap: 0.5 }}>
                <div>
                  <Typography variant='body2SemiBold'>{p.amount}</Typography>
                  &nbsp;
                  <Typography
                    variant='body2SemiBold'
                    sx={{ color: globalColours['$global-colour-sentiment-positive-default'] }}
                  >
                    {p.highlight}
                  </Typography>
                </div>
                <Typography variant='body3'>{p.description}</Typography>
              </Stack>
            </Box>
          ))}
        {prices.length === 1 && (
          <Box sx={{ py: 2, px: 2 }}>
            <Stack sx={{ gap: 0.5 }}>
              <div>
                <Typography variant='body2SemiBold'>{prices[0].amount}</Typography>
              </div>
              <Typography variant='body3'>{prices[0].description}</Typography>
            </Stack>
          </Box>
        )}
      </Stack>

      <Accordion
        expanded={expandedFeatures}
        onChange={() => {}}
        disableGutters
        elevation={0}
        square
        sx={{
          borderTop: `solid 0px transparent`,
          bgcolor: 'transparent',
          '& .MuiButtonBase-root': {
            px: 0,
          },
          '&::before': {
            display: 'none',
          },
          mt: 2,
        }}
      >
        <AccordionSummary
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
          onClick={toggleExpandedFeature}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant='subtitle5'>What you get</Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: globalColours['$global-colour-interactive-primary-defaultå'],
                cursor: 'pointer',
              }}
            >
              <ArrowLeftIcon
                sx={{
                  width: '28px',
                  height: '28px',
                  transform: expandedFeatures ? 'rotate(90deg)' : 'rotate(-90deg)',
                }}
              />
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 0,
            py: 1.5,
            borderTop: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          }}
        >
          <Stack sx={{ gap: '10px', mt: '10px' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  background: globalColours['$global-colour-background-neutral-default'],
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BankNoteIcon sx={{ width: '16px', height: '16px' }} />
              </Box>

              <Typography variant='body3' sx={{ px: 1, py: 0.5 }}>
                <strong>Money back guarantee</strong> for your first month if not satisfied
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  background: globalColours['$global-colour-background-neutral-default'],
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Calendar sx={{ width: '16px', height: '16px' }} />
              </Box>

              <Typography variant='body3' sx={{ px: 1, py: 0.5 }}>
                2 lessons per week, 24 lessons per term
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  background: globalColours['$global-colour-background-neutral-default'],
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <VideocamOutlined sx={{ width: '16px', height: '16px' }} />
              </Box>

              <Typography variant='body3' sx={{ px: 1, py: 0.5 }}>
                Playback recorded lessons offline for self-paced learning
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  background: globalColours['$global-colour-background-neutral-default'],
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <UsersIcon sx={{ width: '16px', height: '16px' }} />
              </Box>

              <Typography variant='body3' sx={{ px: 1, py: 0.5 }}>
                Maximum group class size of 8
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

BookingSummary.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

BookingSummary.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(BookingSummary)(
  ({ theme }) => `
`,
);
